"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
export default function Faq() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: listRef,
    isVisible: listVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    numeral: "01.",
    question: "What age does Montessori start at Little Acorns?",
    answer: "We welcome children starting at 2.5 years of age. Our program is designed to support children through their formative years until they reach age 6, providing a consistent, nurturing environment that grows alongside them."
  }, {
    numeral: "02.",
    question: "How much does Montessori cost in Toronto?",
    answer: "As a proud participant in the Canada-Wide Early Learning and Child Care (CWELCC) system, we are committed to making high-quality Montessori education accessible. Our fees are structured to reflect this partnership, and we provide detailed breakdowns during our tour process."
  }, {
    numeral: "03.",
    question: "Is toilet training required for enrollment?",
    answer: "Yes, we require children to be toilet trained prior to their start date. This supports the independence and self-care skills that are central to the Montessori philosophy and ensures a smooth transition into our classroom routines."
  }, {
    numeral: "04.",
    question: "What is the student-to-teacher ratio?",
    answer: "We maintain a low 1:8 ratio to ensure every child receives personalized attention. This allows our educators to observe each child's unique development and provide the appropriate materials to support their sensitive periods of learning."
  }, {
    numeral: "05.",
    question: "How do I apply for a spot at the school?",
    answer: "The first step is to book a tour to see our classrooms in action. Following your visit, you can submit an application form. We review all applications carefully to ensure our community is the right fit for your family's needs."
  }, {
    numeral: "06.",
    question: "Do you offer full-day and half-day options?",
    answer: "Yes, we offer flexible scheduling options including half-day morning and afternoon sessions, as well as full-day programs. We work with families to find the schedule that best supports their child's rhythm and family life."
  }];
  return <section data-section-id="3573" id="common-questions" className="bg-background py-32 font-default">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-4">
            <div ref={headerRef} className={`lg:sticky lg:top-32 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <p className="text-xs uppercase tracking-widest text-primary font-sans mb-8">
                Frequent Inquiries
              </p>
              <h2 className="font-default text-foreground text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                <span className="block">Common</span>
                <span className="block">Questions,</span>
                <span className="block italic font-serif text-primary">
                  Answered.
                </span>
              </h2>
            </div>
          </div>

          {}
          <div className="lg:col-span-8">
            <div ref={listRef} className={`transition-all duration-700 ease-out ${listVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-b border-border last:border-b-0 group">
                    <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden">
                      <div className="flex items-start gap-6 flex-1 text-left">
                        <span className="text-sm font-mono text-primary tracking-wider pt-2 shrink-0">
                          {faq.numeral}
                        </span>
                        <span className="font-default text-foreground text-2xl leading-snug tracking-tight">
                          {faq.question}
                        </span>
                      </div>
                      <span aria-hidden="true" className="font-default text-3xl font-light text-foreground/70 ml-6 shrink-0 leading-none transition-transform duration-400 ease-out group-data-[state=open]:rotate-45">
                        +
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <div className="pl-0 md:pl-14 pr-0 md:pr-12 py-2">
                        <p className="font-default text-muted-foreground text-[1.0625rem] leading-[1.7]">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>

              {}
              <div className="mt-16 pt-8 border-t border-border">
                <p className="font-default italic text-lg text-muted-foreground">
                  Still have questions?{" "}
                  <Link to="/ContactUsPage" className="text-foreground not-italic font-default relative inline-block group">
                    <span className="inline-block">
                      Contact our team
                      <span className="ml-1 inline-block transition-transform duration-300 group-hover:translate-x-1">
                        &rarr;
                      </span>
                    </span>
                    <span className="absolute left-0 -bottom-0.5 h-px w-full bg-foreground origin-left scale-x-100 group-hover:scale-x-0 transition-transform duration-500 ease-out" />
                    <span className="absolute left-0 -bottom-0.5 h-px w-full bg-primary origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out delay-100" />
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}